import { useEffect, useState } from 'react';
import { Webhook, Plus, Loader2, Copy, Check, Trash2, Send, Power, AlertTriangle, ListChecks } from 'lucide-react';
import {
  listWebhookZiele, createWebhookZiel, updateWebhookZiel, deleteWebhookZiel, testWebhookZiel, webhookProtokoll,
  type WebhookZielDto, type WebhookVersuchDto,
} from '../lib/publicApiApi';
import ConfirmDialog from './admin/ConfirmDialog';

interface Props {
  keyId: number;
  darkMode: boolean;
  inputClasses: string;
}

const zeit = (iso: string) => new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });

/**
 * Webhook-Ziele eines API-Schlüssels. Das Secret erscheint genau einmal nach dem
 * Anlegen — wie beim Schlüssel selbst.
 */
export default function ApiKeyWebhooks({ keyId, darkMode, inputClasses }: Props) {
  const [ziele, setZiele] = useState<WebhookZielDto[]>([]);
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState('');
  const [url, setUrl] = useState('');
  const [events, setEvents] = useState('*');
  const [legtAn, setLegtAn] = useState(false);
  const [neu, setNeu] = useState<{ url: string; secret: string } | null>(null);
  const [kopiert, setKopiert] = useState(false);
  const [testErgebnis, setTestErgebnis] = useState<Record<number, string>>({});
  const [protokollFuer, setProtokollFuer] = useState<number | null>(null);
  const [protokoll, setProtokoll] = useState<WebhookVersuchDto[]>([]);
  const [loeschen, setLoeschen] = useState<WebhookZielDto | null>(null);

  const leise = darkMode ? 'text-slate-400' : 'text-slate-500';
  const rand = darkMode ? 'border-slate-700' : 'border-slate-200';

  const laden = () => {
    setLaedt(true);
    listWebhookZiele(keyId)
      .then(setZiele)
      .catch((e: any) => setFehler(e?.message || 'Webhooks konnten nicht geladen werden'))
      .finally(() => setLaedt(false));
  };
  useEffect(laden, [keyId]);

  const anlegen = async () => {
    if (!url.trim()) return;
    setLegtAn(true); setFehler('');
    try {
      const liste = events.split(',').map(e => e.trim()).filter(Boolean);
      const res = await createWebhookZiel(keyId, url.trim(), liste.length ? liste : ['*']);
      setNeu({ url: res.url, secret: res.secret });
      setUrl(''); setEvents('*');
      laden();
    } catch (e: any) {
      setFehler(e?.message || 'Anlegen fehlgeschlagen');
    } finally {
      setLegtAn(false);
    }
  };

  const testen = async (z: WebhookZielDto) => {
    setTestErgebnis(t => ({ ...t, [z.id]: 'sendet…' }));
    try {
      const r = await testWebhookZiel(keyId, z.id);
      setTestErgebnis(t => ({ ...t, [z.id]: r.ergebnis === 'success' ? `ok (HTTP ${r.httpStatus}, ${r.dauerMs} ms)` : `fehlgeschlagen: ${r.fehler ?? r.httpStatus}` }));
      laden();
      if (protokollFuer === z.id) zeigeProtokoll(z.id);
    } catch (e: any) {
      setTestErgebnis(t => ({ ...t, [z.id]: e?.message || 'fehlgeschlagen' }));
    }
  };

  const umschalten = async (z: WebhookZielDto) => {
    try { await updateWebhookZiel(keyId, z.id, { aktiv: !z.aktiv }); laden(); }
    catch (e: any) { setFehler(e?.message || 'Ändern fehlgeschlagen'); }
  };

  const zeigeProtokoll = async (id: number) => {
    setProtokollFuer(id);
    try { setProtokoll(await webhookProtokoll(keyId, id)); }
    catch (e: any) { setFehler(e?.message || 'Protokoll konnte nicht geladen werden'); }
  };

  return (
    <div className={`mt-2 w-full rounded-lg border p-3 ${rand}`}>
      <div className="flex items-center gap-2 mb-2">
        <Webhook className={`w-4 h-4 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
        <span className={`text-xs font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>Webhooks</span>
        <span className={`text-xs ${leise}`}>— meldet Änderungen sofort an ein anderes System</span>
      </div>

      {neu && (
        <div className={`mb-3 rounded-lg border p-3 ${darkMode ? 'border-amber-800 bg-amber-900/20' : 'border-amber-300 bg-amber-50'}`}>
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
            <span className={`text-xs font-bold ${darkMode ? 'text-amber-300' : 'text-amber-800'}`}>Secret für {neu.url} — jetzt kopieren</span>
          </div>
          <p className={`text-xs mb-2 ${darkMode ? 'text-amber-300/80' : 'text-amber-800/80'}`}>
            Wird nur jetzt angezeigt. Im Empfänger eintragen; damit prüft er die Signatur jedes Aufrufs.
          </p>
          <div className="flex items-center gap-2">
            <code className={`flex-1 min-w-0 px-2.5 py-2 rounded-md text-xs font-mono break-all ${darkMode ? 'bg-slate-900 text-slate-200' : 'bg-white text-slate-800 border border-slate-200'}`}>{neu.secret}</code>
            <button
              onClick={async () => { try { await navigator.clipboard.writeText(neu.secret); setKopiert(true); setTimeout(() => setKopiert(false), 2000); } catch { /* markierbar */ } }}
              className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold text-white ${kopiert ? 'bg-green-600' : 'bg-blue-600 hover:bg-blue-700'}`}
            >
              {kopiert ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}{kopiert ? 'Kopiert' : 'Kopieren'}
            </button>
          </div>
          <button onClick={() => setNeu(null)} className={`mt-2 text-xs font-medium ${leise}`}>Habe ich kopiert — ausblenden</button>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-2 mb-2">
        <input value={url} onChange={e => setUrl(e.target.value)} placeholder="https://… (Ziel-URL)" className={`${inputClasses} flex-1`} />
        <input value={events} onChange={e => setEvents(e.target.value)} placeholder="* oder z. B. vorgang.status_changed, document.created" className={`${inputClasses} sm:w-64`} title="Kommagetrennt; * = alle Ereignisse" />
        <button onClick={anlegen} disabled={legtAn || !url.trim()} className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50">
          {legtAn ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Ziel anlegen
        </button>
      </div>
      {fehler && <p className="text-xs text-red-600 mb-2">{fehler}</p>}

      {laedt ? (
        <div className={`flex items-center gap-2 text-xs ${leise}`}><Loader2 className="w-3.5 h-3.5 animate-spin" /> Wird geladen…</div>
      ) : ziele.length === 0 ? (
        <p className={`text-xs ${leise}`}>Noch kein Ziel.</p>
      ) : (
        <ul className="space-y-2">
          {ziele.map(z => (
            <li key={z.id} className={`rounded-md border px-3 py-2 ${rand}`}>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className={`text-xs font-mono break-all ${darkMode ? 'text-slate-200' : 'text-slate-800'}`}>{z.url}</span>
                <span className={`text-xs ${leise}`}>{z.events.join(', ')}</span>
                {z.aktiv ? (
                  <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${darkMode ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-700'}`}>aktiv</span>
                ) : (
                  <span className="text-xs px-1.5 py-0.5 rounded-full font-medium bg-red-100 text-red-700">{z.deaktiviertAm ? 'automatisch abgeschaltet' : 'aus'}</span>
                )}
                {z.fehlerInFolge > 0 && <span className="text-xs text-amber-600">{z.fehlerInFolge} Fehlschläge in Folge</span>}
                <span className="ml-auto flex flex-wrap gap-1">
                  <button onClick={() => testen(z)} className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium ${darkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-600 hover:bg-slate-100'}`}><Send className="w-3.5 h-3.5" /> Test</button>
                  <button onClick={() => (protokollFuer === z.id ? setProtokollFuer(null) : zeigeProtokoll(z.id))} className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium ${darkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-600 hover:bg-slate-100'}`}><ListChecks className="w-3.5 h-3.5" /> Protokoll</button>
                  <button onClick={() => umschalten(z)} className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium ${darkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-600 hover:bg-slate-100'}`}><Power className="w-3.5 h-3.5" /> {z.aktiv ? 'Ausschalten' : 'Einschalten'}</button>
                  <button onClick={() => setLoeschen(z)} className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-red-600 hover:bg-red-50 dark:hover:bg-slate-700"><Trash2 className="w-3.5 h-3.5" /> Löschen</button>
                </span>
              </div>
              {!z.aktiv && z.deaktiviertGrund && <p className="mt-1 text-xs text-red-600">{z.deaktiviertGrund}</p>}
              {testErgebnis[z.id] && <p className={`mt-1 text-xs ${leise}`}>Test: {testErgebnis[z.id]}</p>}
              {protokollFuer === z.id && (
                <div className="mt-2 overflow-x-auto">
                  {protokoll.length === 0 ? <p className={`text-xs ${leise}`}>Noch keine Zustellung.</p> : (
                    <table className={`w-full text-xs ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                      <thead><tr className={leise}><th className="text-left pr-3">Zeit</th><th className="text-left pr-3">Event</th><th className="text-left pr-3">HTTP</th><th className="text-left pr-3">Dauer</th><th className="text-left pr-3">Versuch</th><th className="text-left">Ergebnis</th></tr></thead>
                      <tbody>
                        {protokoll.map((p, i) => (
                          <tr key={i} title={p.fehler ?? p.deliveryId}>
                            <td className="pr-3 whitespace-nowrap">{zeit(p.am)}</td>
                            <td className="pr-3">{p.event}</td>
                            <td className="pr-3">{p.httpStatus ?? '—'}</td>
                            <td className="pr-3">{p.dauerMs} ms</td>
                            <td className="pr-3">{p.versuch}</td>
                            <td className={p.ergebnis === 'success' ? 'text-green-600' : p.ergebnis === 'retry' ? 'text-amber-600' : 'text-red-600'}>
                              {{ success: 'zugestellt', retry: 'wird wiederholt', failed: 'abgelehnt', aufgegeben: 'aufgegeben' }[p.ergebnis]}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      <ConfirmDialog
        open={!!loeschen}
        title="Webhook-Ziel löschen"
        message={`${loeschen?.url} bekommt ab sofort keine Ereignisse mehr; offene Zustellungen und das Protokoll werden gelöscht.`}
        confirmLabel="Löschen"
        onConfirm={async () => {
          const z = loeschen; setLoeschen(null);
          if (!z) return;
          try { await deleteWebhookZiel(keyId, z.id); laden(); } catch (e: any) { setFehler(e?.message || 'Löschen fehlgeschlagen'); }
        }}
        onCancel={() => setLoeschen(null)}
      />
    </div>
  );
}
